import { useState, useRef, useCallback, useEffect } from "react";
import {
  UploadSimple, MagnifyingGlass, Spinner, Robot, PaperPlaneTilt,
  ChatCircle, ArrowsCounterClockwise, Warning, Plus, Minus, Equals,
  CheckCircle, ArrowLeft, Star, Sparkle, SealQuestion, Info,
  ArrowsOut, X, CaretDown, CaretUp, DownloadSimple, FileCsv, FileXls,
} from "@phosphor-icons/react";
import OneDriveFilePicker from "../common/OneDriveFilePicker";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "../ui/dropdown-menu";
import { toast } from "sonner";
import axios from "axios";
import { API, exportComparisonCsv, exportComparisonExcel } from "../../api";
import MarkdownMessage from "../common/MarkdownMessage";
import ComparisonViewModes from "./ComparisonViewModes";
import SideBySideViewer from "./SideBySideViewer";
import OverlayViewer from "./OverlayViewer";
import NotifyWhenDoneButton from "../common/NotifyWhenDoneButton";

const ACCEPT_EXTS = [".pdf", ".png", ".jpg", ".jpeg", ".webp", ".bmp", ".tiff", ".tif", ".dwg", ".dxf"];
const ACCEPT_STR  = ACCEPT_EXTS.join(",");
const MAX_DRAWINGS = 10;

// ── Annotation colour palette ─────────────────────────────────────────────────
const COLOR_PALETTE = {
  red:    { hex: "#ef4444", label: "Red",    tw: "bg-red-500",    ring: "ring-red-500"    },
  orange: { hex: "#f97316", label: "Orange", tw: "bg-orange-500", ring: "ring-orange-500" },
  green:  { hex: "#22c55e", label: "Green",  tw: "bg-green-500",  ring: "ring-green-500"  },
  blue:   { hex: "#3b82f6", label: "Blue",   tw: "bg-blue-500",   ring: "ring-blue-500"   },
  purple: { hex: "#a855f7", label: "Purple", tw: "bg-purple-500", ring: "ring-purple-500" },
};
const ALL_COLORS = new Set(Object.keys(COLOR_PALETTE));

const SIG_STYLE = {
  HIGH:   { bg: "bg-red-100",   text: "text-red-700"   },
  MEDIUM: { bg: "bg-amber-100", text: "text-amber-700" },
  LOW:    { bg: "bg-blue-100",  text: "text-blue-700"  },
};
const TYPE_STYLE = {
  ADDED:    { label: "ADDED",    icon: Plus,   bg: "bg-green-100",  text: "text-green-700"  },
  REMOVED:  { label: "REMOVED",  icon: Minus,  bg: "bg-red-100",    text: "text-red-700"    },
  MODIFIED: { label: "MODIFIED", icon: Equals, bg: "bg-amber-100",  text: "text-amber-700"  },
};

// ── SVG circles overlay ───────────────────────────────────────────────────────
function CirclesOverlay({ changes, activeColors, activeIndex, onCircleClick, onHover, onLeave }) {
  const visibleAnnotations = changes
    .map((c, idx) => c.latest_annotation
      ? { ...c.latest_annotation, changeIndex: idx, change: c }
      : null
    )
    .filter(ann => ann && activeColors.has(ann.color || "red"));

  if (!visibleAnnotations.length) return null;

  return (
    <svg
      viewBox="0 0 1000 750"
      className="absolute inset-0 w-full h-full"
      style={{ pointerEvents: "none", zIndex: 10 }}
    >
      {visibleAnnotations.map((ann, i) => {
        const cx = ann.x * 1000;
        const cy = ann.y * 750;
        const r  = ann.radius * 1000;
        const hex = COLOR_PALETTE[ann.color]?.hex ?? "#ef4444";
        const isActive = activeIndex === ann.changeIndex;
        return (
          <g key={i} style={{ pointerEvents: "all", cursor: "pointer" }}
            onClick={() => onCircleClick(ann.changeIndex)}
            onMouseEnter={e => onHover(e, ann.changeIndex, ann.change)}
            onMouseLeave={onLeave}
          >
            {/* Glow ring when active */}
            {isActive && (
              <circle cx={cx} cy={cy} r={r + 4} fill="none"
                stroke={hex} strokeWidth={3} opacity={0.35} />
            )}
            <circle cx={cx} cy={cy} r={r} fill="none"
              stroke={hex} strokeWidth={isActive ? 3 : 2}
              opacity={isActive ? 1 : 0.85} />
            {/* Number badge */}
            <circle cx={cx} cy={cy} r={Math.min(r * 0.38, 14)} fill={hex} opacity={0.95} />
            <text x={cx} y={cy + 0.5} textAnchor="middle" dominantBaseline="middle"
              fontSize={Math.min(r * 0.36, 12)} fontWeight="bold" fill="white"
              style={{ pointerEvents: "none" }}>
              {ann.changeIndex + 1}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ── Hover tooltip ─────────────────────────────────────────────────────────────
function AnnotationTooltip({ tooltip, containerRef }) {
  if (!tooltip) return null;
  const change = tooltip.change;
  const ts = TYPE_STYLE[change.type] || TYPE_STYLE.MODIFIED;
  const TypeIcon = ts.icon;
  const color = COLOR_PALETTE[change.latest_annotation?.color] || COLOR_PALETTE.red;

  // Clamp tooltip so it doesn't go off-screen
  const left = Math.min(tooltip.x + 12, (containerRef.current?.offsetWidth ?? 400) - 220);
  const top  = tooltip.y - 8;

  return (
    <div
      className="absolute z-30 bg-slate-900 text-white rounded shadow-xl pointer-events-none max-w-56 min-w-40"
      style={{ left, top, transform: "translateY(-100%)" }}
      data-testid="annotation-tooltip"
    >
      {/* Color stripe */}
      <div className="h-1 rounded-t" style={{ background: color.hex }} />
      <div className="p-2.5 space-y-1">
        <div className="flex items-center gap-1.5">
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-bold rounded ${ts.bg} ${ts.text}`}>
            <TypeIcon size={9} weight="bold" />{ts.label}
          </span>
          <span className="text-xs text-slate-400">#{tooltip.changeIndex + 1}</span>
        </div>
        <p className="text-xs leading-snug">{change.description}</p>
        {change.location && (
          <p className="text-xs text-slate-400">{change.location}</p>
        )}
      </div>
    </div>
  );
}

// ── Color toggle bar ──────────────────────────────────────────────────────────
function ColorToggles({ activeColors, onToggle, changes }) {
  // Count annotations per color
  const counts = {};
  changes.forEach(c => {
    const col = c.latest_annotation?.color;
    if (col) counts[col] = (counts[col] || 0) + 1;
  });

  const usedColors = Object.keys(COLOR_PALETTE).filter(c => counts[c]);
  if (!usedColors.length) return null;

  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="color-toggles">
      <span className="text-xs text-slate-500 font-medium mr-0.5">Filter:</span>
      {usedColors.map(color => {
        const meta    = COLOR_PALETTE[color];
        const active  = activeColors.has(color);
        const count   = counts[color] || 0;
        return (
          <button
            key={color}
            onClick={() => onToggle(color)}
            title={`${active ? "Hide" : "Show"} ${meta.label} annotations`}
            className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border transition-all ${
              active
                ? "text-white border-transparent shadow-sm"
                : "bg-white text-slate-400 border-slate-300"
            }`}
            style={active ? { background: meta.hex, borderColor: meta.hex } : {}}
            data-testid={`color-toggle-${color}`}
          >
            <span className="font-bold">{count}</span>
            {meta.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Annotated drawing viewer (inline + fullscreen) ────────────────────────────
function AnnotatedDrawingViewer({ image, changes, activeChangeIndex, onChangeClick }) {
  const [activeColors, setActiveColors]       = useState(ALL_COLORS);
  const [tooltip, setTooltip]                 = useState(null);
  const [fullscreen, setFullscreen]           = useState(false);
  const containerRef  = useRef(null);
  const fsContainerRef = useRef(null);

  const toggleColor = useCallback((color) => {
    setActiveColors(prev => {
      const next = new Set(prev);
      if (next.has(color)) next.delete(color);
      else next.add(color);
      return next;
    });
  }, []);

  const handleHover = useCallback((e, changeIndex, change) => {
    const ref = fullscreen ? fsContainerRef : containerRef;
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    setTooltip({ x: e.clientX - rect.left, y: e.clientY - rect.top, changeIndex, change });
  }, [fullscreen]);

  const handleLeave = useCallback(() => setTooltip(null), []);

  // Close fullscreen on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") setFullscreen(false); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const overlayProps = { changes, activeColors, activeIndex: activeChangeIndex,
    onCircleClick: onChangeClick, onHover: handleHover, onLeave: handleLeave };

  return (
    <>
      {/* ── Inline viewer ── */}
      <div className="border-b border-slate-200">
        {/* Toolbar */}
        <div className="px-3 py-2 flex items-center justify-between gap-2 bg-primary/5 border-b border-primary/10">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-bold text-primary uppercase tracking-wider">Latest version</span>
            <span className="text-xs text-slate-400">— annotated</span>
          </div>
          <div className="flex items-center gap-2">
            <ColorToggles activeColors={activeColors} onToggle={toggleColor} changes={changes} />
            <button
              onClick={() => setFullscreen(true)}
              className="flex items-center gap-1 px-2 py-1 rounded text-xs text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors border border-slate-200"
              title="Open fullscreen"
              data-testid="fullscreen-btn"
            >
              <ArrowsOut size={13} />
              <span>Fullscreen</span>
            </button>
          </div>
        </div>

        {/* Image + annotations */}
        <div ref={containerRef} className="relative bg-slate-100 select-none"
          style={{ cursor: "crosshair" }}>
          {image
            ? <img src={`data:image/jpeg;base64,${image}`} alt="Latest drawing"
                className="w-full object-contain max-h-80" />
            : <div className="w-full h-48 flex items-center justify-center text-slate-400 text-xs">No preview available</div>
          }
          <CirclesOverlay {...overlayProps} />
          <AnnotationTooltip tooltip={tooltip} containerRef={containerRef} />
        </div>
      </div>

      {/* ── Fullscreen modal ── */}
      {fullscreen && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex flex-col"
          data-testid="fullscreen-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 flex-shrink-0">
            <div className="flex items-center gap-3">
              <span className="text-white font-semibold text-sm">Latest version — Annotated</span>
              <ColorToggles activeColors={activeColors} onToggle={toggleColor} changes={changes} />
            </div>
            <button
              onClick={() => setFullscreen(false)}
              className="text-white/70 hover:text-white p-1.5 rounded hover:bg-white/10 transition-colors"
              data-testid="fullscreen-close"
            >
              <X size={20} />
            </button>
          </div>

          {/* Drawing */}
          <div className="flex-1 overflow-auto flex items-center justify-center p-4">
            <div
              ref={fsContainerRef}
              className="relative select-none"
              style={{ maxHeight: "100%", maxWidth: "100%" }}
            >
              {image && (
                <img
                  src={`data:image/jpeg;base64,${image}`}
                  alt="Latest drawing fullscreen"
                  className="object-contain"
                  style={{ maxHeight: "calc(100vh - 100px)", maxWidth: "100%" }}
                />
              )}
              <CirclesOverlay {...overlayProps} />
              <AnnotationTooltip tooltip={tooltip} containerRef={fsContainerRef} />
            </div>
          </div>

          {/* Footer — change count */}
          <div className="px-4 py-2 border-t border-white/10 flex-shrink-0 text-center">
            <span className="text-white/50 text-xs">
              {changes.filter(c => c.latest_annotation).length} annotated change{changes.filter(c => c.latest_annotation).length !== 1 ? "s" : ""} · click a circle to highlight · press Esc to close
            </span>
          </div>
        </div>
      )}
    </>
  );
}

// ── Drawing card (upload/select phase) ───────────────────────────────────────
function DrawingCard({ drawing, isLatest, isReference, n, onSetLatest, onRemove, disabled }) {
  return (
    <div className={`relative flex flex-col rounded-sm border-2 transition-all overflow-hidden ${
      isLatest ? "border-primary shadow-sm"
      : isReference && n > 2 ? "border-slate-400 shadow-sm"
      : "border-slate-200"
    }`} data-testid={`drawing-card-${drawing.id}`}>
      {/* Thumbnail */}
      <div className="bg-slate-100 aspect-[4/3] flex items-center justify-center overflow-hidden">
        {drawing.loading ? (
          <div className="flex flex-col items-center gap-2 text-slate-400">
            <Spinner size={24} className="animate-spin text-primary" />
            <span className="text-xs">Processing...</span>
          </div>
        ) : drawing.preview ? (
          <img src={`data:image/jpeg;base64,${drawing.preview}`} alt={drawing.filename}
            className="w-full h-full object-contain" />
        ) : (
          <div className="flex flex-col items-center gap-2 text-slate-400">
            <span className="text-3xl">📄</span>
            <span className="text-xs">No preview</span>
          </div>
        )}
      </div>

      {/* Filename */}
      <div className="px-2.5 pt-2 pb-1">
        <p className="text-xs font-medium text-slate-700 truncate" title={drawing.filename}>
          {drawing.filename}
        </p>
      </div>

      {/* Latest selector */}
      <div className="px-2.5 pb-2.5 flex flex-col gap-1.5">
        <button onClick={onSetLatest} disabled={disabled || drawing.loading}
          className={`w-full flex items-center justify-center gap-1.5 py-1.5 px-2 rounded text-xs font-medium border transition-colors disabled:opacity-50 ${
            isLatest
              ? "bg-primary text-white border-primary"
              : "bg-white text-slate-500 border-slate-300 hover:border-primary hover:text-primary"
          }`}
          data-testid={`latest-toggle-${drawing.id}`}>
          <Star size={11} weight={isLatest ? "fill" : "regular"} />
          {isLatest ? "Latest version" : "Mark as latest"}
        </button>

        {/* Reference selector — only shown when 3+ drawings */}
        {n > 2 && (
          <button onClick={() => {/* reference is set by parent */}}
            disabled
            className={`w-full flex items-center justify-center gap-1 py-1 px-2 rounded text-xs border transition-colors ${
              isReference
                ? "bg-slate-600 text-white border-slate-600"
                : "bg-white text-slate-300 border-slate-200"
            }`}
            data-testid={`reference-indicator-${drawing.id}`}>
            {isReference ? "Older version (reference)" : "—"}
          </button>
        )}
      </div>

      {/* Remove */}
      {!disabled && (
        <button onClick={onRemove}
          className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-white/80 hover:bg-red-50 text-slate-400 hover:text-red-500 flex items-center justify-center text-sm font-bold border border-slate-200 transition-colors"
          title="Remove" data-testid={`remove-drawing-${drawing.id}`}>
          ×
        </button>
      )}
    </div>
  );
}

// ── Revision timeline ────────────────────────────────────────────────────────
function RevisionTimeline({ result, drawings }) {
  const { ranking, ranking_confident, ranking_labels, latest_index } = result;
  if (!ranking || ranking.length < 2) return null;

  const readyDrawings = drawings.filter(d => !d.loading);

  return (
    <div className="mt-3" data-testid="revision-timeline">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">
          Version Timeline
        </span>
        {!ranking_confident && (
          <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
            Approximate order
          </span>
        )}
      </div>

      <div className="flex items-stretch gap-0 overflow-x-auto pb-1">
        {ranking.map((drawingIdx, timelinePos) => {
          const drawing = readyDrawings[drawingIdx];
          const label   = ranking_labels[timelinePos] || "";
          const isFirst = timelinePos === 0;
          const isLast  = timelinePos === ranking.length - 1;
          const isLatest = drawingIdx === latest_index;

          return (
            <div key={drawingIdx} className="flex items-center flex-shrink-0">
              {/* Card */}
              <div className={`flex flex-col rounded border overflow-hidden w-24 ${
                isLatest ? "border-primary shadow-sm" : "border-slate-200"
              }`}>
                {/* Thumbnail */}
                <div className="bg-slate-100 h-16 flex items-center justify-center overflow-hidden">
                  {drawing?.preview ? (
                    <img
                      src={`data:image/jpeg;base64,${drawing.preview}`}
                      alt={drawing?.filename || `Drawing ${drawingIdx + 1}`}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <span className="text-xl">📄</span>
                  )}
                </div>
                {/* Label */}
                <div className={`px-1.5 py-1 ${isLatest ? "bg-primary/5" : "bg-white"}`}>
                  <p className="text-xs font-medium truncate text-slate-700"
                    title={drawing?.filename}>
                    {drawing?.filename
                      ? drawing.filename.length > 10
                        ? drawing.filename.substring(0, 9) + "…"
                        : drawing.filename
                      : `Drawing ${drawingIdx + 1}`}
                  </p>
                  {label && (
                    <p className="text-xs text-slate-400 truncate leading-tight" title={label}>
                      {label}
                    </p>
                  )}
                  {isLatest && (
                    <div className="flex items-center gap-0.5 mt-0.5">
                      <Star size={9} weight="fill" className="text-primary" />
                      <span className="text-xs text-primary font-medium">Latest</span>
                    </div>
                  )}
                </div>
                {/* Position badge */}
                <div className={`text-center text-xs py-0.5 font-semibold ${
                  isFirst ? "bg-slate-100 text-slate-500"
                  : isLast  ? "bg-primary text-white"
                  : "bg-slate-50 text-slate-400"
                }`}>
                  {isFirst ? "Oldest" : isLast ? "Newest" : `#${timelinePos + 1}`}
                </div>
              </div>

              {/* Arrow between cards */}
              {timelinePos < ranking.length - 1 && (
                <div className="flex items-center px-1 text-slate-300 flex-shrink-0">
                  <svg width="16" height="12" viewBox="0 0 16 12" fill="none">
                    <path d="M0 6H12M12 6L7 1M12 6L7 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── "Find Most Recent" result panel ──────────────────────────────────────────
function DetectResultPanel({ result, drawings, onAccept, onDismiss }) {
  if (!result) return null;
  const { confident, latest_index, reasoning, evidence, uncertainty_notes,
          possible_latest_index, possible_latest_hint } = result;

  const readyDrawings = drawings.filter(d => !d.loading);
  const suggestedDrawing = latest_index != null ? readyDrawings[latest_index] : null;
  const hintDrawing = possible_latest_index != null ? readyDrawings[possible_latest_index] : null;

  if (confident) {
    return (
      <div className="rounded-sm border border-green-200 bg-green-50 p-3 space-y-2"
        data-testid="detect-result-confident">
        <div className="flex items-start gap-2">
          <CheckCircle size={16} className="text-green-600 flex-shrink-0 mt-0.5" weight="fill" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-green-800">
              Latest drawing identified
              {suggestedDrawing && `: "${suggestedDrawing.filename}"`}
            </p>
            <p className="text-xs text-green-700 mt-1 leading-relaxed">{reasoning}</p>
          </div>
        </div>
        {evidence?.length > 0 && (
          <div className="pl-6 space-y-0.5">
            {evidence.map((e, i) => (
              <p key={i} className="text-xs text-green-700">✓ {e}</p>
            ))}
          </div>
        )}
        <RevisionTimeline result={result} drawings={readyDrawings} />
        <div className="flex gap-2 pt-1">
          <Button size="sm" onClick={onAccept}
            className="text-xs h-7 rounded-sm bg-green-600 hover:bg-green-700 text-white"
            data-testid="accept-latest-btn">
            Apply suggestion
          </Button>
          <Button size="sm" variant="ghost" onClick={onDismiss}
            className="text-xs h-7 rounded-sm text-slate-500 hover:text-slate-700">
            Dismiss
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-sm border border-amber-200 bg-amber-50 p-3 space-y-2"
      data-testid="detect-result-unsure">
      <div className="flex items-start gap-2">
        <SealQuestion size={16} className="text-amber-600 flex-shrink-0 mt-0.5" weight="fill" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-800">Could not determine latest with confidence</p>
          <p className="text-xs text-amber-700 mt-1 leading-relaxed">{reasoning}</p>
        </div>
      </div>

      {uncertainty_notes?.length > 0 && (
        <div className="pl-6 space-y-0.5">
          <p className="text-xs font-semibold text-amber-700">Reasons:</p>
          {uncertainty_notes.map((n, i) => (
            <p key={i} className="text-xs text-amber-700">• {n}</p>
          ))}
        </div>
      )}

      {/* Show partial ranking even if not confident */}
      {result.ranking && <RevisionTimeline result={result} drawings={readyDrawings} />}

      {hintDrawing && possible_latest_hint && (
        <div className="pl-6 mt-1">
          <div className="flex items-start gap-1.5 bg-amber-100 rounded px-2 py-1.5">
            <Info size={12} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700">
              <span className="font-medium">Possible hint:</span> {possible_latest_hint}
            </p>
          </div>
          <div className="flex gap-2 mt-2">
            <Button size="sm" onClick={onAccept}
              className="text-xs h-7 rounded-sm bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="accept-hint-btn">
              Apply hint anyway
            </Button>
            <Button size="sm" variant="ghost" onClick={onDismiss}
              className="text-xs h-7 rounded-sm text-slate-500 hover:text-slate-700">
              Dismiss
            </Button>
          </div>
        </div>
      )}

      {!hintDrawing && (
        <div className="pl-6">
          <Button size="sm" variant="ghost" onClick={onDismiss}
            className="text-xs h-7 rounded-sm text-slate-500">
            Dismiss
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Reference thumbnail (small, for context) ─────────────────────────────────
function ReferenceThumbnail({ image }) {
  const [open, setOpen] = useState(false);
  if (!image) return null;
  return (
    <div className="border-b border-slate-200">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-slate-500 hover:bg-slate-50 transition-colors"
        data-testid="reference-toggle"
      >
        <span>Older version (reference)</span>
        {open ? <CaretUp size={12} /> : <CaretDown size={12} />}
      </button>
      {open && (
        <div className="bg-slate-100">
          <img src={`data:image/jpeg;base64,${image}`} alt="Reference drawing"
            className="w-full object-contain max-h-64" />
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function ComparisonTool({ onBack, restoreSessionId, onStartNew, fasterAnalysis = false }) {
  // drawings: [{id, slot, filename, file, preview, loading}]
  const [drawings, setDrawings]           = useState([]);
  const [latestId, setLatestId]           = useState(null);
  const [phase, setPhase]                 = useState("upload");
  const [compareStartedAt, setCompareStartedAt] = useState(null);
  const [result, setResult]               = useState(null);
  const [activeChangeIndex, setActiveChangeIndex] = useState(null);
  const [messages, setMessages]           = useState([]);
  const [chatInput, setChatInput]         = useState("");
  const [chatLoading, setChatLoading]     = useState(false);
  const [isDragging, setIsDragging]       = useState(false);
  const [detectLoading, setDetectLoading] = useState(false);
  const [detectResult, setDetectResult]   = useState(null);
  const [viewMode, setViewMode]           = useState("annotated");

  const sessionRef   = useRef(null);
  const uploadingRef = useRef(false);
  const slotCounter  = useRef(0);
  const msgCounter   = useRef(0);
  const messagesEndRef = useRef(null);
  const fileInputRef   = useRef(null);
  const [showOneDrivePicker, setShowOneDrivePicker] = useState(false);

  const addMessage = useCallback((role, content) => {
    setMessages(prev => [...prev, { id: ++msgCounter.current, role, content }]);
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  }, []);

  // ── Upload logic ──────────────────────────────────────────────────────────
  const processFiles = useCallback(async (files) => {
    if (uploadingRef.current) return;
    uploadingRef.current = true;

    const validFiles = files.filter(f => {
      const ext = f.name.toLowerCase().substring(f.name.lastIndexOf("."));
      if (!ACCEPT_EXTS.includes(ext)) { toast.error(`Unsupported: ${f.name}`); return false; }
      return true;
    });

    if (!validFiles.length) { uploadingRef.current = false; return; }

    // Check capacity
    let availableSlots = 0;
    setDrawings(prev => { availableSlots = MAX_DRAWINGS - prev.length; return prev; });
    await new Promise(r => setTimeout(r, 0));

    if (availableSlots <= 0) {
      toast.error(`Maximum ${MAX_DRAWINGS} drawings allowed`);
      uploadingRef.current = false;
      return;
    }

    const toProcess = validFiles.slice(0, availableSlots);
    const placeholders = toProcess.map(f => ({
      id: crypto.randomUUID(),
      slot: slotCounter.current++,
      filename: f.name,
      file: f,
      preview: null,
      loading: true,
    }));

    setDrawings(prev => [...prev, ...placeholders]);

    for (const ph of placeholders) {
      try {
        if (!sessionRef.current) {
          const r = await axios.post(`${API}/compare/create`, {});
          sessionRef.current = r.data.session_id;
        }
        const fd = new FormData();
        fd.append("session_id", sessionRef.current);
        fd.append("slot", ph.slot);
        fd.append("file", ph.file);
        const r = await axios.post(`${API}/compare/upload`, fd);

        setDrawings(prev => {
          const updated = prev.map(d =>
            d.id === ph.id ? { ...d, preview: r.data.first_page_image || null, loading: false } : d
          );
          // Auto-select second drawing as latest
          const loaded = updated.filter(d => !d.loading);
          if (loaded.length === 2) {
            setLatestId(loaded[1].id);
          }
          return updated;
        });
        // Clear stale detect result when drawings change
        setDetectResult(null);
      } catch (err) {
        toast.error(`Upload failed: ${ph.filename}`);
        setDrawings(prev => prev.filter(d => d.id !== ph.id));
      }
    }

    uploadingRef.current = false;
  }, []);

  const handleFileInput = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) processFiles(files);
    e.target.value = "";
  }, [processFiles]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length) processFiles(files);
  }, [processFiles]);

  const removeDrawing = useCallback((id) => {
    setDrawings(prev => prev.filter(d => d.id !== id));
    setLatestId(prev => prev === id ? null : prev);
    setDetectResult(null);
  }, []);

  // ── Find Most Recent ──────────────────────────────────────────────────────
  const findLatest = useCallback(async () => {
    if (!sessionRef.current || drawings.filter(d => !d.loading).length < 2) return;
    setDetectLoading(true);
    setDetectResult(null);
    try {
      const fd = new FormData();
      fd.append("session_id", sessionRef.current);
      const r = await axios.post(`${API}/compare/find-latest`, fd);
      setDetectResult(r.data.result);
    } catch (err) {
      toast.error(`Detection failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setDetectLoading(false);
    }
  }, [drawings]);

  const acceptDetectResult = useCallback(() => {
    if (!detectResult) return;
    const idx = detectResult.confident
      ? detectResult.latest_index
      : detectResult.possible_latest_index;
    if (idx == null) return;
    const readyDrawings = drawings.filter(d => !d.loading);
    if (idx < readyDrawings.length) {
      setLatestId(readyDrawings[idx].id);
    }
    setDetectResult(null);
  }, [detectResult, drawings]);

  // ── Run comparison ────────────────────────────────────────────────────────
  const runComparison = useCallback(async () => {
    const readyDrawings = drawings.filter(d => !d.loading);
    const latestDrawing    = readyDrawings.find(d => d.id === latestId);
    const referenceDrawing = readyDrawings.length === 2
      ? readyDrawings.find(d => d.id !== latestId)
      : null; // for N>2, user must have exactly 1 non-latest... we'll pick the first non-latest

    const refDrawing = referenceDrawing || readyDrawings.find(d => d.id !== latestId);
    if (!latestDrawing || !refDrawing) return;

    setPhase("comparing");
    setCompareStartedAt(new Date().toISOString());
    addMessage("system", "Starting comparison. The AI will examine both drawings and may zoom in for details...");

    try {
      let sid = sessionRef.current;

      // If roles mismatch what was uploaded (e.g. user swapped latest after upload),
      // re-upload with correct assignment
      const needsReupload = latestDrawing.slot !== drawings.find(d => d.id === latestId)?.slot;
      // Actually check: we need latest to be at its slot, and reference at its slot.
      // The backend now uses slots directly, so no reupload needed — just pass slot indices.

      const fd = new FormData();
      fd.append("session_id", sid);
      fd.append("latest_slot", latestDrawing.slot);
      fd.append("reference_slot", refDrawing.slot);
      fd.append("faster_analysis", String(fasterAnalysis));
      const r = await axios.post(`${API}/compare/run`, fd);

      if (r.data.success) {
        setResult(r.data.comparison);
        setPhase("complete");
        const n = r.data.comparison.changes?.length ?? 0;
        addMessage("ai", `Comparison complete. I found ${n} change${n !== 1 ? "s" : ""} between the two drawings.\n\n${r.data.comparison.summary || ""}`);
      }
    } catch (err) {
      toast.error(`Comparison failed: ${err.response?.data?.detail || err.message}`);
      setPhase("upload");
      addMessage("system", "Comparison failed. Please try again.");
    }
  }, [drawings, latestId, addMessage]);

  const sendChat = useCallback(async () => {
    const msg = chatInput.trim();
    if (!msg || chatLoading || !sessionRef.current) return;
    setChatInput("");
    addMessage("user", msg);
    setChatLoading(true);
    try {
      const r = await axios.post(`${API}/compare/chat`, { session_id: sessionRef.current, message: msg });
      addMessage("ai", r.data.response);
    } catch {
      addMessage("system", "Failed to get a response.");
    } finally {
      setChatLoading(false);
    }
  }, [chatInput, chatLoading, addMessage]);

  const resetAll = useCallback(() => {
    setDrawings([]);
    setLatestId(null);
    sessionRef.current = null;
    slotCounter.current = 0;
    setPhase("upload");
    setResult(null);
    setMessages([]);
    setActiveChangeIndex(null);
    setDetectResult(null);
    setDetectLoading(false);
    setViewMode("annotated");
    uploadingRef.current = false;
    if (onStartNew) onStartNew();
  }, [onStartNew]);

  // ── Export comparison results (Excel / CSV) ────────────────────────────────
  const [exportingFormat, setExportingFormat] = useState(null); // "csv" | "excel" | null

  const handleExport = useCallback(async (format) => {
    if (!sessionRef.current) return;
    setExportingFormat(format);
    try {
      const fn = format === "csv" ? exportComparisonCsv : exportComparisonExcel;
      const res = await fn(sessionRef.current);
      const mime = format === "csv"
        ? "text/csv"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const blob = new Blob([res.data], { type: mime });
      const disposition = res.headers?.["content-disposition"] || "";
      const match = disposition.match(/filename="?([^";]+)"?/);
      const fallbackName = `comparison_${sessionRef.current.slice(0, 8)}.${format === "csv" ? "csv" : "xlsx"}`;

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = match ? match[1] : fallbackName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported as ${format === "csv" ? "CSV" : "Excel"}`);
    } catch (e) {
      let message = e.message;
      if (e.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          message = JSON.parse(text)?.detail || message;
        } catch (_) { /* keep default message */ }
      } else {
        message = e.response?.data?.detail || message;
      }
      toast.error("Export failed: " + message);
    } finally {
      setExportingFormat(null);
    }
  }, []);

  // ── Restore a historical comparison session ───────────────────────────────
  useEffect(() => {
    if (!restoreSessionId) return;
    (async () => {
      try {
        const [compRes, chatRes] = await Promise.all([
          axios.get(`${API}/compare/${restoreSessionId}`),
          axios.get(`${API}/compare/${restoreSessionId}/chat`),
        ]);
        const comp = compRes.data.comparison || {};
        sessionRef.current = restoreSessionId;

        const drawingsMeta = comp.drawings || [];
        const latestSlot   = comp.comparison_result?.latest_slot    ?? null;

        // Fetch thumbnail previews for each drawing
        const withPreviews = await Promise.all(
          drawingsMeta.map(async (d) => {
            const role = d.slot === latestSlot ? "latest" : "reference";
            let preview = null;
            try {
              const r = await axios.get(`${API}/compare/${restoreSessionId}/page/${role}/1`);
              preview = r.data.image_base64 || null;
            } catch { /* thumbnail fetch failure is non-fatal */ }
            return {
              id: crypto.randomUUID(),
              slot: d.slot,
              filename: d.filename || `Drawing ${d.slot + 1}`,
              file: null,
              preview,
              loading: false,
            };
          })
        );

        setDrawings(withPreviews);

        const latestDrawingObj = withPreviews.find(d => d.slot === latestSlot);
        if (latestDrawingObj) setLatestId(latestDrawingObj.id);

        if (comp.comparison_result) {
          setResult(comp.comparison_result);
          setPhase("complete");
          const n = comp.comparison_result.changes?.length ?? 0;
          addMessage(
            "ai",
            `Loaded from history. ${n} change${n !== 1 ? "s" : ""} identified.\n\n${comp.comparison_result.summary || ""}`
          );
        }

        // Restore chat messages after the initial AI summary
        const msgs = (chatRes.data.messages || [])
          .filter(m => m.role === "user" || m.role === "assistant");
        msgs.forEach(m => addMessage(m.role === "user" ? "user" : "ai", m.content));
      } catch (e) {
        toast.error("Failed to load comparison: " + (e.response?.data?.detail || e.message));
      }
    })();
  }, [restoreSessionId]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const readyDrawings   = drawings.filter(d => !d.loading);
  const latestDrawing   = drawings.find(d => d.id === latestId);
  const refDrawing      = readyDrawings.find(d => d.id !== latestId);
  const anyLoading      = drawings.some(d => d.loading);
  const tooManyDrawings = readyDrawings.length > 2;
  const enoughForDetect = readyDrawings.length >= 2 && !anyLoading;
  const bothReady       = readyDrawings.length === 2 && latestId && !anyLoading;
  const isComparing     = phase === "comparing";

  return (
    <div className="h-full flex flex-col" data-testid="comparison-tool">

      {/* ── Header ── */}
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="text-slate-500 hover:text-slate-800 transition-colors">
            <ArrowLeft size={18} />
          </button>
          <div>
            <h2 className="font-semibold text-slate-800 flex items-center gap-2">
              <ArrowsCounterClockwise size={16} className="text-primary" />
              Drawing Comparison
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {phase === "upload"    && "Upload drawings and select the latest version"}
              {phase === "comparing" && "AI is analysing both drawings..."}
              {phase === "complete"  && `${result?.changes?.length ?? 0} changes identified`}
            </p>
          </div>
        </div>
        {phase === "complete" && (
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!!exportingFormat}
                  className="text-xs gap-1.5 border-slate-300"
                  data-testid="export-dropdown-button"
                >
                  {exportingFormat
                    ? <><Spinner size={12} className="animate-spin" /> Exporting...</>
                    : <><DownloadSimple size={14} /> Export <CaretDown size={12} /></>
                  }
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => handleExport("excel")}
                  disabled={!!exportingFormat}
                  data-testid="export-excel-option"
                >
                  <FileXls size={16} className="text-green-600" />
                  Export as Excel (.xlsx)
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleExport("csv")}
                  disabled={!!exportingFormat}
                  data-testid="export-csv-option"
                >
                  <FileCsv size={16} className="text-slate-500" />
                  Export as CSV
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button size="sm" variant="ghost" onClick={resetAll}
              className="text-xs text-slate-500 hover:text-slate-800"
              data-testid="new-comparison-button">
              New Comparison
            </Button>
          </div>
        )}
      </div>

      {/* ── Upload phase ── */}
      {phase === "upload" && (
        <div className="flex-1 overflow-y-auto p-4 space-y-4">

          {/* Upload zone */}
          {drawings.length < MAX_DRAWINGS && (
            <div className="space-y-2">
              <div
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`w-full border-2 border-dashed rounded-sm p-8 flex flex-col items-center justify-center gap-3 cursor-pointer transition-colors ${
                  isDragging ? "border-primary bg-primary/5" : "border-slate-300 hover:border-primary hover:bg-slate-50"
                }`}
                data-testid="upload-zone"
              >
                <UploadSimple size={28} className="text-slate-400" />
                <div className="text-center">
                  <p className="text-sm font-medium text-slate-700">
                    {drawings.length === 0 ? "Upload your drawings" : `Add more drawings (${drawings.length}/${MAX_DRAWINGS})`}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    {drawings.length === 0
                      ? "Drag & drop or click — select multiple files at once"
                      : `${MAX_DRAWINGS - drawings.length} slot${MAX_DRAWINGS - drawings.length !== 1 ? "s" : ""} remaining`}
                  </p>
                  <p className="text-xs text-slate-300 mt-1">PDF, PNG, JPG, WEBP, BMP, TIFF · up to {MAX_DRAWINGS} drawings</p>
                </div>
                <input ref={fileInputRef} type="file" accept={ACCEPT_STR} multiple
                  className="hidden" onChange={handleFileInput} data-testid="file-input" />
              </div>

              {/* OneDrive option */}
              <button
                onClick={() => setShowOneDrivePicker(true)}
                className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-sm border border-slate-200 bg-white text-slate-500 hover:border-[#0078D4] hover:text-[#0078D4] hover:bg-[#0078D4]/5 text-xs font-medium transition-colors"
                data-testid="compare-onedrive-btn"
              >
                <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 flex-shrink-0" fill="none">
                  <path d="M9.5 7.5C10.83 5.3 13.28 4 16 4C19.87 4 23 7.13 23 11C23 11.34 22.97 11.68 22.93 12C23.56 12.35 24 13.03 24 13.83C24 14.92 23.09 15.83 22 15.83H8.5C6.57 15.83 5 14.26 5 12.33C5 10.51 6.38 9.02 8.17 8.86C8.31 8.23 8.85 7.73 9.5 7.5Z" fill="currentColor" opacity="0.6" />
                  <path d="M3.5 11C1.57 11 0 12.57 0 14.5C0 16.43 1.57 18 3.5 18H19.5C20.88 18 22 16.88 22 15.5C22 14.12 20.88 13 19.5 13H19C18.45 10.2 16 8 13 8C10.73 8 8.73 9.22 7.69 11.06C6.7 10.42 5.15 10.5 4.5 11H3.5Z" fill="currentColor" />
                </svg>
                Add from OneDrive
              </button>
            </div>
          )}

          {/* Drawing cards grid */}
          {drawings.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Your drawings ({drawings.length})
                </p>
              </div>

              <div className={`grid gap-3 ${drawings.length <= 2 ? "grid-cols-2" : drawings.length === 3 ? "grid-cols-3" : "grid-cols-2 sm:grid-cols-5"}`}>
                {drawings.map(drawing => (
                  <DrawingCard
                    key={drawing.id}
                    drawing={drawing}
                    n={readyDrawings.length}
                    isLatest={drawing.id === latestId}
                    isReference={!drawing.loading && drawing.id !== latestId && readyDrawings.length === 2}
                    onSetLatest={() => setLatestId(drawing.id)}
                    onRemove={() => removeDrawing(drawing.id)}
                    disabled={isComparing}
                  />
                ))}
              </div>

              {readyDrawings.length > 2 && (
                <p className="text-xs text-slate-400 text-center">
                  The drawing marked "Latest version" will be compared against the drawing you select as the older reference.
                  {!latestId && " — Select the latest version first."}
                </p>
              )}
            </div>
          )}

          {/* Find Most Recent + Detect result panel */}
          {enoughForDetect && (
            <div className="space-y-3">
              {detectResult && (
                <DetectResultPanel
                  result={detectResult}
                  drawings={readyDrawings}
                  onAccept={acceptDetectResult}
                  onDismiss={() => setDetectResult(null)}
                />
              )}
              <button
                onClick={findLatest}
                disabled={detectLoading}
                className="w-full flex items-center justify-center gap-2 py-2 px-4 rounded-sm text-xs font-medium border border-slate-300 bg-white text-slate-500 hover:bg-slate-50 hover:border-slate-400 hover:text-slate-600 disabled:opacity-50 transition-colors"
                data-testid="find-latest-btn"
              >
                {detectLoading
                  ? <><Spinner size={12} className="animate-spin" /> Analysing drawings...</>
                  : <><Sparkle size={12} weight="fill" /> Find Most Recent Version</>
                }
              </button>
            </div>
          )}

          {tooManyDrawings && (
            <div className="flex items-start gap-2 px-3 py-2.5 rounded-sm bg-amber-50 border border-amber-200 text-amber-800 text-xs">
              <Warning size={14} className="flex-shrink-0 mt-0.5" weight="fill" />
              Comparison only works with 2 drawings. Remove {readyDrawings.length - 2} drawing{readyDrawings.length - 2 !== 1 ? "s" : ""} to continue.
            </div>
          )}

          <Button
            onClick={runComparison}
            disabled={!bothReady}
            className="w-full rounded-sm py-6 text-base font-semibold bg-primary hover:bg-[#002175] transition-all shadow-sm hover:shadow-md disabled:opacity-40"
            data-testid="start-comparison-button"
          >
            <MagnifyingGlass size={18} className="mr-2" />
            Compare Drawings
          </Button>
        </div>
      )}

      {/* ── Comparing phase ── */}
      {isComparing && (
        <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8">
          <div className="spinner" />
          <p className="text-slate-600 text-sm text-center">
            AI is examining both drawings...<br />
            <span className="text-slate-400 text-xs">The agent may zoom in to inspect fine details</span>
          </p>
          <NotifyWhenDoneButton
            jobType="comparison"
            sessionId={sessionRef.current}
            startedAt={compareStartedAt}
          />
        </div>
      )}

      {/* ── Results phase ── */}
      {phase === "complete" && result && (
        <div className="flex-1 overflow-y-auto">
          {/* View mode switcher */}
          <ComparisonViewModes activeMode={viewMode} onModeChange={setViewMode} />

          {/* Annotated view (default) */}
          {viewMode === "annotated" && (
            <>
              <AnnotatedDrawingViewer
                image={latestDrawing?.preview || ""}
                changes={result.changes || []}
                activeChangeIndex={activeChangeIndex}
                onChangeClick={setActiveChangeIndex}
              />
              <ReferenceThumbnail image={refDrawing?.preview || ""} />
            </>
          )}

          {/* Side-by-side view */}
          {viewMode === "side-by-side" && (
            <SideBySideViewer
              sessionId={sessionRef.current}
              changes={result.changes || []}
            />
          )}

          {/* Red/Blue overlay view */}
          {viewMode === "overlay" && (
            <OverlayViewer sessionId={sessionRef.current} />
          )}

          {result.overall_assessment && (
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
              <p className="text-xs text-slate-600 leading-relaxed">
                <span className="font-semibold text-slate-800">Assessment: </span>
                {result.overall_assessment}
              </p>
            </div>
          )}

          <div className="p-4 space-y-2">
            <h3 className="text-xs font-bold text-slate-600 uppercase tracking-wider flex items-center gap-2">
              <Warning size={14} className="text-warning" />
              Changes ({result.changes?.length ?? 0})
            </h3>
            {(result.changes ?? []).map((change, idx) => {
              const ts = TYPE_STYLE[change.type] || TYPE_STYLE.MODIFIED;
              const ss = SIG_STYLE[change.significance] || SIG_STYLE.MEDIUM;
              const TypeIcon = ts.icon;
              const isActive = activeChangeIndex === idx;
              return (
                <div key={idx}
                  onClick={() => (change.reference_annotation || change.latest_annotation) && setActiveChangeIndex(isActive ? null : idx)}
                  className={`border rounded-sm p-3 transition-colors ${isActive ? "border-primary bg-primary/5" : "border-slate-200 hover:border-slate-300"} ${(change.reference_annotation || change.latest_annotation) ? "cursor-pointer" : ""}`}
                  data-testid={`change-item-${idx}`}>
                  <div className="flex items-start gap-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold rounded flex-shrink-0 ${ts.bg} ${ts.text}`}>
                      <TypeIcon size={10} weight="bold" />{ts.label}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-800">{change.description}</p>
                      {change.location && <p className="text-xs text-slate-400 mt-1">{change.location}</p>}
                    </div>
                    <span className={`flex-shrink-0 text-xs px-1.5 py-0.5 rounded ${ss.bg} ${ss.text} font-medium`}>
                      {change.significance}
                    </span>
                  </div>
                </div>
              );
            })}

            {result.changes?.length === 0 && (
              <div className="text-center py-6">
                <CheckCircle size={32} className="mx-auto text-green-500 mb-2" />
                <p className="text-sm text-slate-600">No changes detected.</p>
              </div>
            )}

            {result.unchanged_elements?.length > 0 && (
              <div className="mt-4">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                  Unchanged ({result.unchanged_elements.length})
                </h3>
                {result.unchanged_elements.map((el, idx) => (
                  <div key={idx} className="flex items-start gap-2 py-1 text-xs text-slate-500">
                    <CheckCircle size={12} className="text-green-500 mt-0.5 flex-shrink-0" />{el}
                  </div>
                ))}
              </div>
            )}
          </div>

          {messages.length > 0 && (
            <div className="px-4 pb-2 space-y-3">
              {messages.map((msg) => (
                <div key={msg.id} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] ${
                    msg.role === "user" ? "message-user px-4 py-3"
                    : msg.role === "system" ? "bg-slate-100 text-slate-600 px-4 py-2 rounded text-sm"
                    : "message-ai px-4 py-3"
                  }`}>
                    {msg.role === "ai" && (
                      <div className="flex items-center gap-2 mb-1">
                        <Robot size={14} className="text-primary" />
                        <span className="text-xs font-medium text-primary">AI Assistant</span>
                      </div>
                    )}
                    {msg.role === "ai" ? (
                      <MarkdownMessage content={msg.content} />
                    ) : (
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    )}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      )}

      {/* ── Chat ── */}
      {phase === "complete" && (
        <div className="qa-input flex-shrink-0">
          <div className="flex items-center gap-2 mb-2">
            <ChatCircle size={14} className="text-primary" />
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Ask about the comparison</span>
          </div>
          <div className="flex gap-2">
            <Input value={chatInput} onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
              placeholder="e.g. Why was the dimension changed?"
              className="flex-1 rounded-sm text-sm" disabled={chatLoading}
              data-testid="comparison-chat-input" />
            <Button onClick={sendChat} disabled={!chatInput.trim() || chatLoading}
              size="sm" className="rounded-sm bg-primary hover:bg-primary-hover flex-shrink-0"
              data-testid="comparison-chat-send">
              {chatLoading ? <Spinner size={15} className="animate-spin" /> : <PaperPlaneTilt size={15} />}
            </Button>
          </div>
        </div>
      )}

      {/* OneDrive File Picker */}
      {showOneDrivePicker && (
        <OneDriveFilePicker
          onSelect={(file) => processFiles([file])}
          onClose={() => setShowOneDrivePicker(false)}
        />
      )}
    </div>
  );
}
